import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {ToolbarButton} from "../ToolbarButton";
import './NewBuildingToolbar.css'
import {checkIfCentralQuarter} from "../../../utils/buildings/checkIfCentralQuarter";
import {checkIfAnyBuildingToken} from "../../../utils/buildings/checkIfAnyBuildingToken";
import {checkIfConstructionAllowedInRound} from "../../../utils/gameState/construction/checkIfConstructionAllowedInRound";
import {ANY_COMMON_BUILDING_TOKEN, ANY_SPECIAL_BUILDING_TOKEN} from "../../../constants/tokens";
import BuildingDescription from "../../BuildingDescription/BuildingDescription";
import {addClassNameIfTrue} from "../../../utils/other/addClassNameIfTrue";
import {MASKED_ICON_TONES, MaskedIcon} from '../../coloredIcons/MaskedIcon/MaskedIcon';
import {useBuildingHints} from '../../../hooks/visuals/useBuildingHints';
import {useDesktopLayout, useMinWidthLayout} from '../../../hooks/visuals/useWideScreenLayout';
import {getTurnColorPlayer} from '../../../utils/players/getTurnColorPlayer';
import {getPlayerColorStyle} from '../../../utils/gameUi/getPlayerColorStyle';
import {joinClassNames} from '../../gameUi/joinClassNames';
import CentralQuarterIcon from '../CentralQuarterIcon/CentralQuarterIcon';
import TokenHero from '../TokenHero/TokenHero';
import {
  countTokenGroupGaps,
  getTokenRowSlots,
  TokenCaption,
  TokenGlint,
  TokenPrice,
  TokenSpotlight
} from '../TokenMuster/TokenMuster';

// С этой ширины полный список строений — один ряд, уже — два
const ONE_ROW_LIST_MIN_WIDTH_PX = 769;
// Уход с ряда — с задержкой: зазоры между жетонами не должны возвращать описание
const HOVER_CLOSE_DELAY_MS = 170;

// Кнопки «Спецстроения»/«Назад» своего описания не несут: о них рассказывает LIST_BUTTON_CARDS
const checkIfHasDescription = (tokenId) => !checkIfAnyBuildingToken(tokenId);

// На кнопках переключения списка карточка рассказывает о строениях этого списка в целом
const LIST_BUTTON_CARDS = {
  [ANY_SPECIAL_BUILDING_TOKEN.id]: {
    title: 'Особые строения',
    description: (
      <div className={'policy-description_line'}>
        Особые усиляющие строения, которые строятся поверх{' '}
        <img
          className={'policy-description-icon-building policy-description-icon-building-margin-left'}
          src={'/img/hexes/quarter.f69660fa.png'}
          alt={'Базовый квартал'}
          title={'Базовый квартал'}
        />.
      </div>
    ),
  },
  [ANY_COMMON_BUILDING_TOKEN.id]: {
    title: 'Обычные строения',
    description: (
      <div className={'policy-description_line'}>
        Основные строения для развития города, снабжения и найма отрядов.
      </div>
    ),
  },
};

const getDescriptionTitle = (building) => {
  if (LIST_BUTTON_CARDS[building.id]) {
    return LIST_BUTTON_CARDS[building.id].title;
  }
  return checkIfCentralQuarter(building.id) ? 'Центр. квартал' : building.label;
};

const renderHeroToken = (building) => (
  checkIfCentralQuarter(building.id)
    ? <CentralQuarterIcon token={building.id}/>
    : <img src={building.buttonImage || building.image} alt={building.label}/>
);

// Жетоны по порядку ряда: первый жетон блока и кнопка переключения списка отделены от
// предыдущих
const getRowTokens = (groups) => groups.flatMap((group, groupIndex) => (
  group.map((token, index) => ({
    ...token,
    isGroupStart: (groupIndex > 0 && index === 0) || checkIfAnyBuildingToken(token.id),
  }))
));

/**
 * Список строений фазы строительства с ценами — общая разметка панели своего
 * хода (NewBuildingToolbar) и панели плана в чужой ход (PlanningBuildingToolbar).
 * Сама ничего не диспатчит: выбор уходит в onSelect (tokenId, isDisabledFirstRound).
 *
 * Жетоны — рядом с ценой на каждом (TokenMuster). Описание выбранного строения —
 * карточка с крупным жетоном: на десктопе слева от ряда, вне десктопа над ним. Оно нужно
 * новичку, поэтому переключается кнопкой «?» в шапке футера (см. useBuildingHints: по
 * умолчанию включено первые партии, выбор запоминается); выключенное описание не занимает
 * места, и тогда в ряду все строения сразу. На десктопе описание показывает и строение
 * под мышью
 */
const NewBuildingToolbarView = ({buildings, buildingGroups, selectedToken, currentRound, availableCoins, onSelect}) => {
  const [isHintsVisible] = useBuildingHints();
  const isDesktopLayout = useDesktopLayout();
  const isOneRowListFits = useMinWidthLayout(ONE_ROW_LIST_MIN_WIDTH_PX);
  const colorPlayer = useSelector(getTurnColorPlayer);
  const [hoveredId, setHoveredId] = useState(null);
  const hoverTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(hoverTimerRef.current), []);

  const selectedBuilding = buildings.find(b => b.id === selectedToken);
  const hoveredBuilding = buildings.find(b => b.id === hoveredId);

  // Что показывает пятно и подпись: строение под мышью важнее выбранного
  const pointedId = hoveredBuilding ? hoveredId : selectedToken;

  // Что описывает карточка. Выбранный жетон может не найтись в текущем списке (например,
  // в selectedToken остался спецстроение при показанном списке обычных, либо жетон
  // прошлого матча до автовыбора): если в списке он есть, но без описания — карточки нет.
  // На кадр переключения списка, пока автовыбор не сработал, остается прежнее строение
  const lastDescribedRef = useRef(null);
  let describedBuilding = null;
  if (isHintsVisible) {
    if (hoveredBuilding && (checkIfHasDescription(hoveredBuilding.id) || LIST_BUTTON_CARDS[hoveredBuilding.id])) {
      describedBuilding = hoveredBuilding;
    } else if (selectedBuilding) {
      describedBuilding = checkIfHasDescription(selectedBuilding.id) ? selectedBuilding : null;
    } else if (!selectedToken) {
      describedBuilding = lastDescribedRef.current;
    }
    // Кнопка не запоминается: на кадр переключения по ней остается строение
    if (describedBuilding && !LIST_BUTTON_CARDS[describedBuilding.id]) {
      lastDescribedRef.current = describedBuilding;
    }
  }

  const isAllBuildingsList = Boolean(buildingGroups && buildingGroups.length > 1);
  const rowTokens = getRowTokens(buildingGroups || [buildings]);
  // Полный список на телефоне — в два ряда: обычные и специальные
  const isTwoRows = isAllBuildingsList && !isOneRowListFits;
  const columns = isTwoRows ? Math.max(...buildingGroups.map((group) => group.length)) : undefined;
  const rowSlots = getTokenRowSlots(rowTokens, columns);

  const handleTokenPointerEnter = (event, tokenId) => {
    if (!isDesktopLayout || event.pointerType !== 'mouse') {
      return;
    }
    clearTimeout(hoverTimerRef.current);
    setHoveredId(tokenId);
  };

  const handleRowPointerLeave = () => {
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setHoveredId(null), HOVER_CLOSE_DELAY_MS);
  };

  const captionBuilding = isDesktopLayout && isAllBuildingsList && buildings.find(b => b.id === pointedId);

  return (
    <div
      className={`new-buildings-toolbar ${addClassNameIfTrue(isHintsVisible, 'new-buildings-toolbar_with-hints')} ${
        addClassNameIfTrue(isAllBuildingsList, 'new-buildings-toolbar_all')
      }`}
      style={getPlayerColorStyle(colorPlayer?.color)}
    >
      {describedBuilding && (
        <div className={'new-buildings-toolbar_card'}>
          {/* Название под крупным жетоном и не шире его: так плита ниже */}
          <div className={'new-buildings-toolbar_hero'}>
            <TokenHero animationKey={describedBuilding.id} isFramed={true}>
              {renderHeroToken(describedBuilding)}
            </TokenHero>
            <div
              key={describedBuilding.id}
              className={joinClassNames(
                'new-buildings-toolbar_selected-building-name',
                'token-hero-name',
                getDescriptionTitle(describedBuilding).includes(' ') && 'new-buildings-toolbar_selected-building-name_two-lines',
              )}
            >
              {getDescriptionTitle(describedBuilding)}
            </div>
          </div>

          <div className={'new-buildings-toolbar_building-info'}>
            <div
              key={describedBuilding.id}
              className={'new-buildings-toolbar_building-description compact-building-description token-hero-lines'}
            >
              {LIST_BUTTON_CARDS[describedBuilding.id]
                ? LIST_BUTTON_CARDS[describedBuilding.id].description
                : <BuildingDescription buildingId={describedBuilding.id} detailedMode={false}/>}
            </div>

            {/* Невидимые описания остальных строений списка в той же ячейке
                (NewBuildingToolbar.css): колонка — по самому высокому из них, и
                высота плиты не меняется при выборе другого строения */}
            {buildings.filter(b => b.id !== describedBuilding.id && checkIfHasDescription(b.id)).map(building => (
              <div
                key={building.id}
                className={'new-buildings-toolbar_building-description new-buildings-toolbar_building-description_sizer compact-building-description'}
                aria-hidden={'true'}
              >
                <BuildingDescription buildingId={building.id} detailedMode={false}/>
              </div>
            ))}
          </div>

          <span className={'new-buildings-toolbar_delimiter'} aria-hidden={true}/>
        </div>
      )}

      <div
        className={joinClassNames(
          'new-buildings-toolbar_buildings',
          'token-muster',
          isTwoRows && 'new-buildings-toolbar_buildings_two-rows',
        )}
        style={{
          '--token-count': columns || rowTokens.length,
          '--token-groups': isTwoRows ? 0 : countTokenGroupGaps(rowTokens),
          '--token-half-columns': isTwoRows ? columns * 2 : undefined,
        }}
        onPointerLeave={isDesktopLayout ? handleRowPointerLeave : undefined}
      >
        <TokenSpotlight spot={rowSlots[pointedId]}/>
        {captionBuilding && <TokenCaption spot={rowSlots[pointedId]} text={getDescriptionTitle(captionBuilding)}/>}

        {rowTokens.map((token, index) => {
          // Правило первого раунда — то же, по которому строительство
          // разрешает resolveConstructionPlacement
          const isDisabledFirstRound = !checkIfConstructionAllowedInRound(token.id, currentRound);
          const isNavigationToken = checkIfAnyBuildingToken(token.id);
          // Не по карману — приглушена только цена, как на рынке политик:
          // строение можно выбрать и прочитать, поставить не даст проверка
          const isUnaffordable = !isNavigationToken
            && typeof availableCoins === 'number' && token.priceCoins > availableCoins;

          return (
            <div
              key={token.id}
              className={joinClassNames(
                'new-buildings-toolbar_building-container',
                'token-muster_item',
                isDisabledFirstRound && 'view-disabled',
                isUnaffordable && 'new-buildings-toolbar_building-container_unaffordable',
                isUnaffordable && 'token-muster_item_unaffordable',
                !isTwoRows && rowSlots[token.id].isGroupStart && 'token-muster_item_group-start',
              )}
              style={{
                '--slot-index': index,
                // В сетке двух рядов жетон занимает две половинные колонки: короткий ряд
                // встает по центру и при нечетной разнице в числе жетонов
                ...(isTwoRows && {
                  gridColumn: `${rowSlots[token.id].slot * 2 + 1} / span 2`,
                  gridRow: rowSlots[token.id].row + 1,
                }),
              }}
              onClick={() => onSelect(token.id, isDisabledFirstRound)}
              onPointerEnter={(event) => handleTokenPointerEnter(event, token.id)}
              title={token.label}
              data-tutorial-select={token.id}
            >
              <ToolbarButton
                tokenId={token.id}
                selected={selectedToken === token.id}
                image={token.buttonImage ? token.buttonImage : token.image}
                label={token.label}
              >
                <TokenGlint/>
                <TokenPrice price={isNavigationToken ? undefined : token.priceCoins}>
                  {token.isDiscount && (
                    <img
                      className={'toolbar_discount'}
                      src={'/img/icons/percent white.png'}
                      alt={'Скидка'}
                      title={'Скидка'}
                    />
                  )}
                  {token.isFreeAction && (
                    <img
                      className={'toolbar_free-action'}
                      src={'/img/icons/free action.png'}
                      alt={'Свободное действие'}
                      title={'Свободное действие (не учитывается лимитом действий в Фазе Строительства)'}
                    />
                  )}
                  {isNavigationToken && (
                    <MaskedIcon
                      className={'new-buildings-toolbar_navigation-arrow'}
                      src={'/img/icons/arrow down.png'}
                      alt={'Свернуть панель'}
                      title={'Свернуть панель'}
                      tone={MASKED_ICON_TONES.CONTROLS}
                    />
                  )}
                </TokenPrice>
              </ToolbarButton>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NewBuildingToolbarView;
